/**
 * src/shell/providers/AuthProvider.tsx
 *
 * Core Auth Provider & State Machine.
 * States: UNAUTHENTICATED | AUTHENTICATED | LOCKED | REAUTH_REQUIRED.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserAccount } from '../auth/auth-types';
import { multiAccountVault } from '../auth/multi-account-vault';
import { TokenStorage } from '../auth/services/TokenStorage';

export type AuthStatus = 'UNAUTHENTICATED' | 'AUTHENTICATED' | 'LOCKED' | 'REAUTH_REQUIRED';

export interface AuthContextValue {
  status: AuthStatus;
  currentAccount: UserAccount | null;
  allAccounts: UserAccount[];
  signIn: (userId: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  switchAccount: (userId: string) => Promise<boolean>;
  lock: () => void;
  unlock: () => void;
  refreshAccounts: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [vaultState, setVaultState] = useState(multiAccountVault.getState());
  const [status, setStatus] = useState<AuthStatus>(
    vaultState.currentUser ? 'AUTHENTICATED' : 'UNAUTHENTICATED',
  );

  const refreshAccounts = useCallback(() => {
    setVaultState(multiAccountVault.getState());
  }, []);

  useEffect(() => {
    const handleVaultChange = (updated: any) => {
      setVaultState({ ...updated });
      if (updated.currentUser && status !== 'LOCKED') {
        setStatus('AUTHENTICATED');
      } else if (!updated.currentUser) {
        setStatus('UNAUTHENTICATED');
      }
    };

    multiAccountVault.on('auth:stateChanged', handleVaultChange);
    return () => {
      multiAccountVault.off('auth:stateChanged', handleVaultChange);
    };
  }, [status]);

  const signIn = async (userId: string): Promise<boolean> => {
    const success = multiAccountVault.login(userId);
    if (success) {
      const user = multiAccountVault.getCurrentUser();
      if (user?.sessionToken) {
        TokenStorage.setAccessToken(new TextEncoder().encode(user.sessionToken), user.id);
        await TokenStorage.storeRefreshToken(user.id, `refresh_${user.id}_secret`);
      }
      setStatus('AUTHENTICATED');
      refreshAccounts();
      return true;
    }
    return false;
  };

  const signOut = async (): Promise<void> => {
    const current = multiAccountVault.getCurrentUser();
    if (current) {
      await TokenStorage.clearSession(current.id);
    }
    multiAccountVault.logout();
    setStatus('UNAUTHENTICATED');
    refreshAccounts();
  };

  const switchAccount = async (userId: string): Promise<boolean> => {
    const prev = multiAccountVault.getCurrentUser();
    if (prev) {
      TokenStorage.clearAccessToken(); // Zero previous token buffer in memory
    }
    return signIn(userId);
  };

  const lock = () => {
    setStatus('LOCKED');
  };

  const unlock = () => {
    if (multiAccountVault.getCurrentUser()) {
      setStatus('AUTHENTICATED');
    } else {
      setStatus('UNAUTHENTICATED');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        currentAccount: vaultState.currentUser,
        allAccounts: vaultState.accounts,
        signIn,
        signOut,
        switchAccount,
        lock,
        unlock,
        refreshAccounts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    const state = multiAccountVault.getState();
    return {
      status: state.currentUser ? 'AUTHENTICATED' : 'UNAUTHENTICATED',
      currentAccount: state.currentUser,
      allAccounts: state.accounts,
      signIn: async (u) => multiAccountVault.login(u),
      signOut: async () => multiAccountVault.logout(),
      switchAccount: async (u) => multiAccountVault.login(u),
      lock: () => {},
      unlock: () => {},
      refreshAccounts: () => {},
    };
  }
  return ctx;
};
